import { AlertFactory, AlertState } from 'alerts/Alert'
import { UpdateProfileParams } from 'iam/MemberStore'
import Member, { MemberMetadata } from 'iam/models/Member'
import { action, computed, observable, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { areDeepEqual } from 'utils/object-util'
import { func, pattern, required, validationContext } from 'validx'
import { IamPort } from '../ProfileViewModel'
import RolesSelectionViewModelAdapter from '../RolesSelectionViewModelAdapter'
import SelectionViewModel from '../SelectionViewModel'
import SkillsSelectionViewModelAdapter from '../SkillsSelectionViewModelAdapter'

/**
 * View model for the General tab.
 */
export default class ProfileGeneralViewModel {
  @observable givenName = ''
  @observable familyName = ''
  @observable email = ''
  @observable phone = ''
  @observable memberMetadata: MemberMetadata = {}

  /**
   * Skill selection.
   */
  skillSelection = new SelectionViewModel(
    new SkillsSelectionViewModelAdapter(
      this.newAlert,
      this.member,
      this.iamPort,
    ),
  )

  /**
   * Role selection.
   */
  roleSelection = new SelectionViewModel(
    new RolesSelectionViewModelAdapter(
      this.newAlert,
      this.member,
      this.iamPort,
    ),
  )

  /**
   * Validation.
   */
  validation = validationContext(this, {
    givenName: [
      required(),
      func(({ value }) => {
        return (
          value.length <= 40 || "First name can't be more than 40 characters"
        )
      }),
    ],
    familyName: [
      required(),
      func(({ value }) => {
        return (
          value.length <= 40 || "Last name can't be more than 40 characters"
        )
      }),
    ],
    email: [
      required(),
      pattern('email'),
      func(({ value }) => {
        return value.length <= 320 || "Email can't be more than 320 characters"
      }),
    ],
    phone: [
      func(
        (ctx) =>
          // Only run the phone number rule if we have a value,
          // as phone numbers are optional.
          !ctx.value ||
          pattern(
            // Super basic regex for phone number chars.
            /^[+]?[ 0-9()-]+$/i,
            'This does not look like a phone number.',
          )(ctx),
      ),
      func(({ value }) => {
        return value.length <= 20 || "Phone can't be more than 20 characters"
      }),
    ],
  })

  /**
   * Constructor.
   */
  constructor(
    private newAlert: AlertFactory,
    private member: Member,
    private iamPort: IamPort,
  ) {
    makeObservable(this)
  }

  @computed
  get inputFields() {
    return this.member.workspace.customMetaFields.memberMetadataFields.filter(
      (field) => field.active && field.permissions.includes('USER_READ'),
    )
  }

  /**
   * Whether we can save.
   */
  @computed
  get canSave() {
    return !this.saveProfile.pending && this.profileHasChanged && this.canEdit
  }

  @computed
  get canEdit() {
    return !this.member.workspace.config.signup?.external_profiles
  }

  /**
   * Whether the profile has changed.
   */
  @computed
  get profileHasChanged() {
    return !areDeepEqual(
      this.collectParamsFromMember(),
      this.collectParamsForUpdate(),
    )
  }

  /**
   * Activates the view model.
   */
  activate() {
    this.fetchMemberInfo()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    //
  }

  /**
   * Fetch the member.
   */
  fetchMemberInfo = task(async () => {
    await this.iamPort.fetchMemberDetails(this.member)
    this.initializeFromMember()
  })

  /**
   * Saves the
   */
  saveProfile = task.resolved(async () => {
    if (!this.profileHasChanged) {
      return
    }

    if (!this.validation.reset().validate().isValid) {
      return
    }

    try {
      await this.iamPort.updateProfile(
        this.member,
        this.collectParamsForUpdate(),
      )
      // Reload from the member.
      this.initializeFromMember()
      this.newAlert()
        .withState(AlertState.Success)
        .saying('Success!', 'Your profile has been updated.')
        .autoDismiss()
        .show()
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  })

  @action
  onMetadataChange(fieldId: string, value: string | number | boolean | null) {
    this.memberMetadata[fieldId] = value
  }

  /**
   * Collects params for updating.
   */
  private collectParamsForUpdate(): UpdateProfileParams {
    return {
      givenName: this.givenName,
      familyName: this.familyName,
      email: this.email,
      phone: this.phone,
      memberMetadata: this.memberMetadata,
    }
  }

  /**
   * Collects params from the member. Used to compare with entered values in order to determine if
   * there has been any changes.
   */
  private collectParamsFromMember(): UpdateProfileParams {
    return {
      givenName: this.member.givenName,
      familyName: this.member.familyName,
      email: this.member.email,
      phone: this.member.phone,
      memberMetadata: this.member.memberMetadata,
    }
  }

  /**
   * Initializes fields from the membr.
   */
  @action.bound
  private initializeFromMember() {
    this.givenName = this.member.givenName
    this.familyName = this.member.familyName
    this.email = this.member.email
    this.phone = this.member.phone
    this.memberMetadata = {
      ...this.member.memberMetadata,
    }
  }
}

/**
 * Tabs for the General view.
 */
export const enum GeneralTab {
  Profile,
  Skills,
}

export interface MemberMetadataInput {
  type: 'TEXT' | 'NUMERIC' | 'BOOL'
  title: string
  description?: string
  id: string
  active: boolean
  permissions: Array<'ADMIN_WRITE' | 'USER_READ' | 'USER_WRITE'>
}
